import {
  IonButton,
  IonCard,
  IonCardContent,
  IonItem,
  IonLabel,
  IonList,
  IonSelect,
  IonSelectOption,
  useIonRouter,
} from "@ionic/react";
import React, { useContext, useState, useEffect, useRef } from "react";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";
import CardExpandableHeader from "../components/CardExpandableHeader";
import { getCurrentPlant } from "../util/ApiOptionsHelper";
import { getColumnsWithGroup } from "../api/Windows";
import { encodeParam } from "../util/ApiHelper";
import { StoresProps } from "../models/stores/Stores";
import { ColumnsWithGroupAndFilteredFields } from "../models/windows/GetColumnsWithGroup";
import {
  getCascadeDependencyMap,
  getDependentObjects,
} from "../util/GetColumnsWithGroupHelper";
import {
  getCascadeRequiredValuesForPlant,
  getStoreAccountCascadeValues,
} from "../util/StoresAccountHelper";
import {
  CascadeRequiredValue,
  CascadeValue,
} from "../models/analysiscodes/AnalysisCodes";
import { extractFieldsAndValues } from "../util/MetadataHelper";
import {
  applyCascadeRequired,
  isFieldRequired,
  updateCascadeChildOptions,
} from "../util/MetadataHelper";
import { getValidValueDisplayText } from "../util/DropdownDisplayModeHelper";

const StoresAccount: React.FC<StoresProps> = (props) => {
  const { translations } = useContext(TranslationsContext);
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const { expanded, setExpanded, storesTab } = props;
  const router = useIonRouter();
  const [metadataError, setMetadataError] = useState(false);
  const [metadata, setMetadata] = useState<ColumnsWithGroupAndFilteredFields[]>(
    []
  );
  const [values, setValues] = useState<{ [key: string]: any }>({});
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [cascadeValues, setCascadeValues] = useState<{
    [key: string]: CascadeValue[];
  }>({});
  // US 906024 - the plant's cascade "required" rules, matched as each code is picked.
  const [cascadeRequiredValues, setCascadeRequiredValues] = useState<
    CascadeRequiredValue[]
  >([]);

  //Cascade Dependency Map
  const dependencyMap = useRef(new Map<string, string[]>());

  const handleExpand = (expanded: boolean) => {
    setExpanded(expanded);
  };

  const createRequestData = async () => {
    const data = {
      IncludeValidValues: true,
      IncludeValidValuesExceptions: [],
      IsReadOnly: true,
      ValidValueFilters: [],
      WindowName: "mob_inventory_issue_acct",
      ContextPKey: {
        Plant: await getCurrentPlant(),
        WoBase: "",
        WoTask: "  ",
        WoSubtask: "  ",
      },
    };
    return data;
  };

  useEffect(() => {
  (async () => {
    const data = await createRequestData();
    const response = await getColumnsWithGroup(data);
    if (response.status !== 200) {
      setMetadataError(true);
    } else if (response.data) {
      const { fields, values } = extractFieldsAndValues(response);
      // Ensure fields have FieldOrder property for GetColumnsWithGroupField type
      const fieldsWithOrder = fields.map((f: any, idx: number) => ({
        FieldOrder: f.FieldOrder ?? idx,
        ...f,
      }));
      dependencyMap.current = getCascadeDependencyMap(fieldsWithOrder);
      const cascadeValues = await getStoreAccountCascadeValues(
        fieldsWithOrder.map((f) => f.PropertyName ?? "")
      );
      setCascadeValues(cascadeValues);
      setCascadeRequiredValues(await getCascadeRequiredValuesForPlant());
      setMetadata(fieldsWithOrder);
      setValues(values);
    }
  })();
}, []);

  /**
   * US 906024 - a field mapping can make an otherwise optional field mandatory for the code the
   * user just picked. Proceeding is blocked until those fields are filled; a field that is
   * required by its own element setting is unaffected by the mapping and stays required either
   * way.
   */
  const unmetRequiredFields = metadata.filter(
    (field) =>
      isFieldRequired(field) &&
      !values[field.PropertyName ?? ""]
  );

  const onIssueClick = () => {
    let issueDetails = "";
    let keyVals = Object.entries(values);
    keyVals.forEach((innerArray) => {
      if (
        innerArray.length > 1 &&
        innerArray[1] !== "" &&
        innerArray[1] !== null &&
        innerArray[1] !== undefined
      ) {
        issueDetails = `${issueDetails.length > 0 ? `${issueDetails}+` : ""}${
          innerArray[0]
        }+${innerArray[1]}`;
      }
    });
    router.push(`/Stores/Accounts/${encodeParam(issueDetails)}/${storesTab}`);
  };

  return (
    <IonCard>
      <CardExpandableHeader
        expanded={expanded}
        onExpandClick={handleExpand}
        title={translations["lbl_account"] || "Account"}
      />
      <IonCardContent hidden={!expanded} style={{ paddingTop: 0 }}>
        {metadataError && (
          <IonLabel color="danger">
            {translatedMessages["ErrRetrievingFields"]?.MessageText ||
              "Error retrieving the fields for the form"}
          </IonLabel>
        )}
        <IonList>
          {metadata.map((field, index) => {
            const {
              PropertyName,
              TranslatedIdText,
              CascadeChild,
              CascadeParent,
              filteredValues,
            } = field;
            const name = PropertyName ?? "";
            const required = isFieldRequired(field);
            let currVal = values[name];
            let selectVal = filteredValues?.find((validVal: any) => {
              return validVal.Code === currVal;
            });
            return (
              <IonItem key={`stores-account-${index}`}>
                <IonLabel
                  position="floating"
                  color={required ? "danger" : undefined}
                >
                  {TranslatedIdText}
                  {required ? " *" : ""}
                </IonLabel>
                <IonSelect
                  interface="popover"
                  value={currVal}
                  selectedText={
                    selectVal
                      ? getValidValueDisplayText(
                          selectVal,
                          field.DropdownDisplayMode
                        )
                      : ""
                  }
                  placeholder={translations["lbl_btn_select"] || "Select"}
                  onIonChange={(event) => {
                    const { value } = event.detail;
                    const child = metadata.find(
                      (m) => m.PropertyName === CascadeChild
                    );

                    if (CascadeChild && child && cascadeValues[name]?.length) {
                      updateCascadeChildOptions(child, cascadeValues[name], value, setMetadata);
                    }
                    let cascadedDataToRest: { [key: string]: string } = {};
                    const cascadeChild = getDependentObjects(
                      name,
                      dependencyMap.current
                    );
                    cascadeChild.forEach((c) => {
                      cascadedDataToRest = { ...cascadedDataToRest, [c]: "" };
                    });

                    /* US 906024 - re-evaluate whether this code makes the child field
                       mandatory. Deliberately NOT inside the guard above: the flag can be set
                       on a code that maps no child values at all, which is the originating
                       case - pick an account and department becomes mandatory without
                       restricting which departments are allowed. */
                    applyCascadeRequired(
                      cascadeRequiredValues,
                      name,
                      value,
                      values[CascadeParent ?? ""],
                      child,
                      Object.keys(cascadedDataToRest),
                      setMetadata
                    );

                    setValues((pre) => ({
                      ...pre,
                      ...cascadedDataToRest,
                      [name]: value,
                    }));
                    PropertyName !== "Project" && setButtonDisabled(false); //Enable search only when an Account/Area/Department is selected.
                  }}
                >
                  {[...(filteredValues ?? [])]
                    .sort((a: any, b: any) =>
                      (a.Code ?? "").localeCompare(b.Code ?? "")
                    )
                    .map((value: any) => {
                      return (
                        <IonSelectOption key={value.Code} value={value.Code}>
                          {getValidValueDisplayText(
                            value,
                            field.DropdownDisplayMode
                          )}
                        </IonSelectOption>
                      );
                    })}
                </IonSelect>
              </IonItem>
            );
          })}
          <IonButton
            color="primary"
            disabled={buttonDisabled || unmetRequiredFields.length > 0}
            expand="full"
            onClick={onIssueClick}
          >
            {translations["lbl_btn_next"] || "Next"}
          </IonButton>
        </IonList>
      </IonCardContent>
    </IonCard>
  );
};

export default StoresAccount;
